import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSalesforce } from '../hooks/useSalesforce'
import { useSubscription } from '../hooks/useSubscription'
import { useQuickStart } from '../context/QuickStartContext'
import { useToast } from '../context/ToastContext'
import Navbar from '../components/Navbar'
import Tooltip from '../components/Tooltip'
import { supabase } from '../services/supabaseClient'
import { getAuthToken } from '../services/salesforceService'
import { withAuthCheck } from '../utils/salesforceAuth'
import { ReauthModal } from '../components/ReauthModal'
import Chatbot from '../components/Chatbot'
import { callEdgeFunction, isEdgeFunctionMigrated, getEdgeFunctionName } from '../lib/edgeFunctionClient'
import { getEnvironmentLimitsInfo } from '../services/environmentService'
import { isEnvironmentPlan } from '../config/environmentLimits'
import { ChartBarIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import AddEnvironmentModal from '../components/AddEnvironmentModal'

export default function OrgsPage() {
  const { user, signOut } = useAuth()
  const { orgs, isLoading, error, disconnectOrg, syncOrg, refetch, isSuperAdmin, userRole } = useSalesforce()
  const { subscription } = useSubscription()
  const { markStepComplete, stepsCompleted } = useQuickStart()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [syncStatus, setSyncStatus] = useState<{ [key: string]: { status: string; message: string; progress?: number; syncToken?: string } }>({})
  const [summaryStatus, setSummaryStatus] = useState<{ [key: string]: { count: number; total: number; processing: boolean } }>({})
  const [reauthOrg, setReauthOrg] = useState<{ id: string; name: string } | null>(null)
  const [limitsInfo, setLimitsInfo] = useState<any>(null)
  const [expandedEnvironments, setExpandedEnvironments] = useState<Set<string>>(new Set())
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(null)
  const [showAddEnvironmentModal, setShowAddEnvironmentModal] = useState(false)

  // Get current account ID from localStorage
  useEffect(() => {
    const accountId = localStorage.getItem('currentAccountId');
    setCurrentAccountId(accountId);
  }, [])

  // Auto-expand environments with orgs on load
  useEffect(() => {
    if (orgs.length > 0 && expandedEnvironments.size === 0) {
      const envKeys = Object.keys(groupedOrgs);
      setExpandedEnvironments(new Set(envKeys));
    }
  }, [orgs.length]);

  // Mark Quick Start step 1 complete when org is connected
  useEffect(() => {
    if (orgs.length > 0 && !stepsCompleted.orgConnected) {
      markStepComplete('orgConnected');
    }
  }, [orgs.length, stepsCompleted.orgConnected, markStepComplete]);

  // Check if an org is being synced from the URL params
  const syncingOrgId = searchParams.get('syncingOrg')

  // Group orgs by production_org_id (environment)
  const groupedOrgs = orgs.reduce((acc: Record<string, typeof orgs>, org) => {
    const envKey = org.production_org_id || org.org_id || 'standalone';
    if (!acc[envKey]) {
      acc[envKey] = [];
    }
    acc[envKey].push(org);
    return acc;
  }, {});

  // Sort orgs within each environment: production first, then sandboxes
  Object.values(groupedOrgs).forEach(envOrgs => {
    envOrgs.sort((a, b) => {
      if (a.is_sandbox === b.is_sandbox) return 0;
      return a.is_sandbox ? 1 : -1;
    });
  });

  // Create environment list (including empty slots for environment plans)
  const environmentList: Array<{ key: string; name: string; orgs: typeof orgs; isEmpty: boolean }> = [];
  let hasUnusedEnvironments = false;

  if (limitsInfo?.isEnvironmentPlan) {
    // For environment plans (Pro/Enterprise), show all allocated environments
    const usedEnvironments = Object.keys(groupedOrgs);
    const environmentLimit = limitsInfo.limit || 0;

    // Add environments with orgs
    usedEnvironments.forEach(envKey => {
      environmentList.push({
        key: envKey,
        name: envKey,
        orgs: groupedOrgs[envKey],
        isEmpty: false
      });
    });

    // Add empty environment slots
    const emptySlots = Math.max(0, environmentLimit - usedEnvironments.length);
    hasUnusedEnvironments = emptySlots > 0;

    for (let i = 0; i < emptySlots; i++) {
      environmentList.push({
        key: `empty-${i}`,
        name: `Environment ${usedEnvironments.length + i + 1}`,
        orgs: [],
        isEmpty: true
      });
    }
  } else {
    // For non-environment plans, just show standalone orgs grouped
    Object.entries(groupedOrgs).forEach(([envKey, orgs]) => {
      environmentList.push({
        key: envKey,
        name: envKey,
        orgs: orgs,
        isEmpty: false
      });
    });
  }

  const toggleEnvironmentExpansion = (envKey: string) => {
    const newExpanded = new Set(expandedEnvironments);
    if (newExpanded.has(envKey)) {
      newExpanded.delete(envKey);
    } else {
      newExpanded.add(envKey);
    }
    setExpandedEnvironments(newExpanded);
  };

  // Load environment limits info
  useEffect(() => {
    async function loadLimitsInfo() {
      if (currentAccountId) {
        try {
          const limits = await getEnvironmentLimitsInfo(
            currentAccountId,
            subscription?.subscription_plan || null
          );
          console.log('📊 Environment limits:', limits);
          setLimitsInfo(limits);
        } catch (error) {
          console.error('Error loading environment limits:', error);
        }
      }
    }
    loadLimitsInfo();
  }, [currentAccountId, subscription?.subscription_plan, orgs.length]);

  // Set up real-time subscription for sync status updates
  useEffect(() => {
    if (syncingOrgId) {
      setSyncStatus(prev => ({
        ...prev,
        [syncingOrgId]: { status: 'starting', message: 'Initializing sync...' }
      }))
    }
    
    // Subscribe to flow definitions changes for real-time updates
    const subscription = supabase
      .channel('flow-sync-updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'sf_flow_definitions',
        filter: syncingOrgId ? `org_id=eq.${syncingOrgId}` : undefined
      }, (payload) => {
        // Update sync progress based on database changes
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          setSyncStatus(prev => ({
            ...prev,
            [payload.new.org_id]: {
              status: 'syncing',
              message: `Syncing flows... (${payload.new.name})`,
              progress: undefined // Could calculate based on expected count
            }
          }))
        }
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public', 
        table: 'summary_queue',
        filter: syncingOrgId ? `org_id=eq.${syncingOrgId}` : undefined
      }, (payload) => {
        // Track AI summary progress
        if (payload.new && payload.new.org_id) {
          const orgId = payload.new.org_id
          
          // Update summary status based on queue changes
          setSummaryStatus(prev => {
            const current = prev[orgId] || { count: 0, total: 0, processing: false }
            
            if (payload.eventType === 'INSERT') {
              return {
                ...prev,
                [orgId]: { ...current, total: current.total + 1, processing: true }
              }
            } else if (payload.eventType === 'UPDATE' && payload.new.status === 'completed') {
              return {
                ...prev,
                [orgId]: { ...current, count: current.count + 1 }
              }
            }
            
            return prev
          })
        }
      })
      .subscribe()
    
    return () => {
      subscription.unsubscribe()
    }
  }, [syncingOrgId])
  
  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }
  
  const handleDeleteOrg = async (orgId) => {
    if (window.confirm('Are you sure you want to disconnect this Salesforce org?')) {
      try {
        await disconnectOrg(orgId)
        refetch()
      } catch (error) {
        console.error('Error disconnecting org:', error)
      }
    }
  }
  
  // Continue batch sync in the background - DEPRECATED: Using streamlined sync now
  /*
  const continueBatchSync = async (orgId: string, syncToken: string) => {
    try {
      let response;
      const endpoint = '/api/salesforce/sync-flows-batch';
      
      if (isEdgeFunctionMigrated(endpoint)) {
        const functionName = getEdgeFunctionName(endpoint);
        if (functionName) {
          const data = await callEdgeFunction(functionName, {
            orgId,
            continuationToken: syncToken
          });
          response = {
            ok: true,
            json: async () => data
          };
        }
      } else {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orgId,
            continuationToken: syncToken
          })
        });
      }
      
      if (!response.ok) {
        throw new Error('Continuation sync failed');
      }
      
      const result = await response.json();
      
      setSyncStatus(prev => ({
        ...prev,
        [orgId]: { 
          status: 'syncing', 
          message: `Synced ${result.totalProcessed} flows total...`,
          syncToken: result.syncToken
        }
      }));
      
      if (result.hasMore) {
        // Continue syncing
        setTimeout(() => continueBatchSync(orgId, result.syncToken), 1000);
      } else {
        // Sync complete
        setSyncStatus(prev => ({
          ...prev,
          [orgId]: { 
            status: 'completed', 
            message: `Sync complete! ${result.totalProcessed} flows synced.`
          }
        }));
        
        // Refresh the orgs list
        setTimeout(() => {
          refetch();
          setSyncStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[orgId];
            return newStatus;
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Error in continuation sync:', error);
      setSyncStatus(prev => ({
        ...prev,
        [orgId]: { 
          status: 'error', 
          message: `Error: ${error.message || 'Unknown error'}`
        }
      }));
    }
  };
  */
  
  const handleResetOrgSync = async (orgId) => {
    try {
      console.log(`Manually resetting sync status for org ${orgId}`);
      
      // Show confirmation first
      const confirmReset = window.confirm("Reset the stuck sync status? This will allow you to try again.");
      if (!confirmReset) {
        console.log('Reset cancelled by user');
        return;
      }
      
      // Show a loading indicator
      const resetButton = document.getElementById(`reset-button-${orgId}`);
      if (resetButton) {
        resetButton.innerText = "Resetting...";
        resetButton.disabled = true;
      }
      
      // Direct database update to force status reset
      console.log(`Updating database record for org ${orgId}...`);
      const { error } = await supabase
        .from('sf_orgs')
        .update({
          last_synced_at: new Date().toISOString(),
          needs_reconnect: true  // Mark as needing reconnect to show a clear status
        })
        .eq('id', orgId);
      
      if (error) {
        console.error('Error resetting org sync status:', error);
        alert(`Error resetting sync status: ${error.message}\nTry refreshing the page.`);
      } else {
        console.log('Successfully reset sync status to failed in database');
        alert('Sync status has been reset. The page will now reload to clear the state.');
        
        // Force a full page reload to reset the app state
        window.location.reload();
      }
    } catch (err) {
      console.error('Error in reset process:', err);
      alert(`Error during reset: ${err.message}\nTry disconnecting and reconnecting the org.`);
    }
  }
  
  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={handleSignOut} />

      <div className="py-10">
        <header>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between">
              <h1 className="text-3xl font-bold leading-tight text-gray-900">Salesforce Organizations</h1>
              {(userRole === 'Super-Admin' || userRole === 'Admin') && (
                <button 
                  onClick={() => {
                    // Navigate to connection page
                    navigate('/connect-salesforce');
                  }}
                  type="button" 
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Connect Salesforce Org
                </button>
              )}
            </div>
          </div>
        </header>
        <main>
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="px-4 py-8 sm:px-0">
              {isLoading ? (
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="bg-red-50 p-4 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error loading Salesforce organizations</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{error.message}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : orgs.length === 0 ? (
                <div className="border-4 border-dashed border-gray-200 rounded-lg p-8">
                  <div className="text-center">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Welcome to Flow Documentation Manager</h3>
                    <div className="mt-2 max-w-xl text-sm text-gray-500 mx-auto">
                      <p>You haven't connected any Salesforce orgs yet. Connect your first org to get started.</p>
                    </div>
                    <div className="mt-5 space-y-4">
                      {userRole === 'Viewer' ? (
                        <p className="text-sm text-gray-500">
                          You need Admin permissions to connect a Salesforce org. Please contact your administrator.
                        </p>
                      ) : (
                        <button
                          onClick={() => {
                            navigate('/connect-salesforce');
                          }}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          Connect Salesforce Org
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Standard Plan Limits - Only show for non-environment plans */}
                  {limitsInfo && !isEnvironmentPlan(subscription?.subscription_plan) && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-900">
                          Connected Orgs: {limitsInfo.displayText}
                        </div>
                        {limitsInfo.currentCount >= limitsInfo.limit && (
                          <button
                            onClick={() => navigate('/upgrade')}
                            className="text-sm font-medium text-blue-600 hover:text-blue-700"
                          >
                            Upgrade to Professional
                          </button>
                        )}
                      </div>
                      {limitsInfo.currentCount >= limitsInfo.limit && (
                        <p className="mt-2 text-sm text-gray-600">
                          Upgrade to Professional to connect multiple environments with sandboxes
                        </p>
                      )}
                    </div>
                  )}

                  {/* Environment-based Org List */}
                  <div className="space-y-4">
                    {environmentList.map((environment) => {
                      const isExpanded = expandedEnvironments.has(environment.key);
                      const hasOrgs = environment.orgs.length > 0;

                      return (
                        <div key={environment.key} className="bg-white shadow overflow-hidden sm:rounded-lg">
                          {/* Environment Header */}
                          <div
                            className="px-6 py-4 bg-gray-50 cursor-pointer hover:bg-gray-100 flex items-center justify-between border-b border-gray-200"
                            onClick={() => toggleEnvironmentExpansion(environment.key)}
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                              )}
                              <div>
                                <h3 className="text-lg font-medium text-gray-900">{environment.name}</h3>
                                {hasOrgs && (
                                  <p className="text-sm text-gray-500">
                                    {environment.orgs.length} org{environment.orgs.length !== 1 ? 's' : ''}
                                  </p>
                                )}
                                {environment.isEmpty && (
                                  <p className="text-sm text-gray-500">Empty - ready to connect an org</p>
                                )}
                              </div>
                            </div>

                            {/* Per-Environment Add Org Button */}
                            {(userRole === 'Super-Admin' || userRole === 'Admin') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent expansion toggle
                                  navigate('/connect-salesforce');
                                }}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                                Add Org
                              </button>
                            )}
                          </div>

                          {/* Orgs List (when expanded) */}
                          {isExpanded && (
                            <ul className="divide-y divide-gray-200">
                              {environment.orgs.map((org) => (
                      <li key={org.id}>
                        <div className="px-4 py-5 sm:px-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg leading-6 font-medium text-gray-900">{org.name}</h3>
                              <p className="mt-1 max-w-2xl text-sm text-gray-500">{org.instance_url}</p>
                              <div className="mt-2 flex items-center text-sm text-gray-500">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                  {org.is_sandbox ? 'Sandbox' : 'Production'}
                                </span>
                                {org.connection_type === 'integration' && (
                                  <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    Integration User
                                  </span>
                                )}
                                
                                {/* Show sync status if available */}
                                {syncStatus[org.id] && (
                                  <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    syncStatus[org.id].status === 'starting' ? 'bg-blue-100 text-blue-800' :
                                    syncStatus[org.id].status === 'syncing' ? 'bg-yellow-100 text-yellow-800 animate-pulse' :
                                    syncStatus[org.id].status === 'completed' ? 'bg-green-100 text-green-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {syncStatus[org.id].message}
                                  </span>
                                )}
                                
                                {/* Show AI summary status if available */}
                                {summaryStatus[org.id] && summaryStatus[org.id].processing && (
                                  <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                    AI Summaries: {summaryStatus[org.id].count}/{summaryStatus[org.id].total}
                                  </span>
                                )}
                                
                                <span className="ml-2 text-gray-400">
                                  Last synced: {org.last_synced_at ? new Date(org.last_synced_at).toLocaleString() : 'Never'}
                                </span>
                              </div>
                            </div>
                            <div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => navigate(`/flows?orgId=${org.id}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                  View Flows
                                </button>
                                {(userRole === 'Super-Admin' || userRole === 'Admin' || userRole === 'Editor') && (
                                  <button
                                    onClick={() => navigate(`/${org.id}/org-settings`)}
                                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                  >
                                    Settings
                                  </button>
                                )}
                                {(userRole === 'Super-Admin' || userRole === 'Admin' || userRole === 'Editor') && (
                                  <div className="flex space-x-2">
                                    <Tooltip content="Check Salesforce for new and updated flows">
                                      <button
                                        onClick={async () => {
                                        console.log(`Checking for updates for org ${org.id}`);
                                        setSyncStatus(prev => ({
                                          ...prev,
                                          [org.id]: { status: 'checking', message: 'Checking for updates...' }
                                        }));
                                        
                                        await withAuthCheck(
                                          org.id,
                                          async () => {
                                            try {
                                              // Get fresh tokens
                                              const tokens = await getAuthToken(org.id);
                                          
                                          // Call check updates endpoint
                                          const endpoint = '/api/salesforce/flows/check-updates-enhanced';
                                          let result;
                                          
                                          if (isEdgeFunctionMigrated(endpoint)) {
                                            // Use Edge Function
                                            const functionName = getEdgeFunctionName(endpoint);
                                            result = await callEdgeFunction(functionName!, {
                                              orgId: org.id,
                                              access_token: tokens.access_token,
                                              instance_url: tokens.instance_url
                                            });
                                          } else {
                                            // Fallback to Vercel API
                                            const response = await fetch(endpoint, {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({
                                                orgId: org.id,
                                                access_token: tokens.access_token,
                                                instance_url: tokens.instance_url
                                              })
                                            });
                                            
                                            if (!response.ok) {
                                              throw new Error('Failed to check for updates');
                                            }
                                            
                                            result = await response.json();
                                          }
                                          console.log('Update check complete:', result);
                                          
                                          if (result.has_updates) {
                                            // Navigate to flows page with sync preview
                                            navigate(`/flows?orgId=${org.id}&showSyncPreview=true`, {
                                              state: { syncPreviewData: result }
                                            });
                                          } else {
                                            // Show toast notification
                                            showToast('All flows are up to date!', 'info');

                                            setSyncStatus(prev => ({
                                              ...prev,
                                              [org.id]: {
                                                status: 'completed',
                                                message: 'All flows are up to date!'
                                              }
                                            }));

                                            setTimeout(() => {
                                              setSyncStatus(prev => {
                                                const newStatus = { ...prev };
                                                delete newStatus[org.id];
                                                return newStatus;
                                              });
                                            }, 3000);
                                          }
                                            } catch (error: any) {
                                              console.error('Error checking for updates:', error);
                                              
                                              // Check if this is an auth error that should trigger reauth
                                              if (error.message === 'INVALID_SESSION' || 
                                                  error.requiresReauth || 
                                                  error.needsReconnect ||
                                                  error.message?.includes('expired') ||
                                                  error.message?.includes('reconnect')) {
                                                // Re-throw to trigger the auth error handler
                                                throw error;
                                              }
                                              
                                              setSyncStatus(prev => ({
                                                ...prev,
                                                [org.id]: { 
                                                  status: 'error', 
                                                  message: `Error: ${error.message || 'Unknown error'}`
                                                }
                                              }));
                                            }
                                          },
                                          (authError) => {
                                            // Handle auth errors
                                            setReauthOrg({ id: org.id, name: org.name });
                                            setSyncStatus(prev => ({
                                              ...prev,
                                              [org.id]: { 
                                                status: 'error', 
                                                message: 'Authentication expired - please reconnect'
                                              }
                                            }));
                                          }
                                        );
                                      }}
                                      disabled={syncStatus[org.id]?.status === 'checking' || syncStatus[org.id]?.status === 'syncing' || syncStatus[org.id]?.status === 'starting'}
                                      className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                                        syncStatus[org.id]?.status === 'checking'
                                          ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                          : 'text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                                      }`}
                                      >
                                        {syncStatus[org.id]?.status === 'checking' ? 'Checking...' : 'Check for New and Updated Flows'}
                                      </button>
                                    </Tooltip>
                                  </div>
                                )}
                                {userRole === 'Super-Admin' && (
                                  <button
                                    onClick={() => handleDeleteOrg(org.id)}
                                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                  >
                                    Disconnect
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}

                    {/* Promotional Card - Pro/Enterprise: Add Another Environment (only if no unused slots) */}
                    {limitsInfo?.isEnvironmentPlan && !hasUnusedEnvironments && (
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-dashed border-blue-300 overflow-hidden sm:rounded-lg hover:border-blue-400 transition-colors">
                        <div className="px-6 py-6">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0">
                                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                                  <svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                </div>
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                  Need another production environment?
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                  Each environment includes 1 production org + up to 4 related sandboxes. Add more environments to connect additional production orgs.
                                </p>
                                <button
                                  onClick={() => setShowAddEnvironmentModal(true)}
                                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-sm"
                                >
                                  Add More Environments
                                  <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Promotional Card - Standard: Upgrade to Professional */}
                    {limitsInfo && !limitsInfo.isEnvironmentPlan && (
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-dashed border-purple-300 overflow-hidden sm:rounded-lg hover:border-purple-400 transition-colors">
                        <div className="px-6 py-6">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0">
                                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                                  <svg className="h-6 w-6 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                </div>
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                  Want to add more orgs?
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                  Upgrade to our Professional plan to connect additional orgs related to this one, and purchase add-on environments to add new production orgs.
                                </p>
                                <button
                                  onClick={() => navigate('/plans')}
                                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 shadow-sm"
                                >
                                  Upgrade to Professional
                                  <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
      
      {/* Re-authentication Modal */}
      {reauthOrg && (
        <ReauthModal
          isOpen={!!reauthOrg}
          onClose={() => setReauthOrg(null)}
          orgId={reauthOrg.id}
          orgName={reauthOrg.name}
        />
      )}

      {/* Add Environment Modal */}
      <AddEnvironmentModal
        isOpen={showAddEnvironmentModal}
        onClose={() => setShowAddEnvironmentModal(false)}
        onSuccess={() => {
          setShowAddEnvironmentModal(false);
          // Refresh limits info to show new environment count
          if (currentAccountId) {
            getEnvironmentLimitsInfo(
              currentAccountId,
              subscription?.subscription_plan || null
            ).then(limits => setLimitsInfo(limits));
          }
        }}
      />

      {/* Chatbot */}
      <Chatbot />
    </div>
  )
}